import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from 'react-query';
import { getAlbumDetail, getAlbumTracks, updateAlbum, mergeAsCompilation, mergeAlbumInto } from '../api';
import TrackTable from '../components/TrackTable';
import NavControls from '../components/NavControls';
import usePlayerStore from '../store/playerStore';
import useAuthStore from '../store/authStore';

export default function AlbumDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { playQueue } = usePlayerStore();
  const { user } = useAuthStore();
  const isAdmin = user?.is_staff === true;

  const [merging, setMerging] = React.useState(false);
  const [editingTitle, setEditingTitle] = React.useState(false);
  const [titleValue, setTitleValue] = React.useState('');
  const titleInputRef = React.useRef(null);

  const toggleCompilation = async () => {
    if (!album.is_compilation) {
      setMerging(true);
      try {
        await mergeAsCompilation(id);
      } finally {
        setMerging(false);
      }
    } else {
      await updateAlbum(id, { is_compilation: false });
    }
    queryClient.invalidateQueries(['album', id]);
    queryClient.invalidateQueries(['album-tracks', id]);
    queryClient.invalidateQueries('albums');
  };

  const { data: album, isLoading: albumLoading } = useQuery(
    ['album', id],
    () => getAlbumDetail(id).then(r => r.data)
  );

  const { data: tracksData, isLoading: tracksLoading } = useQuery(
    ['album-tracks', id],
    () => getAlbumTracks(id).then(r => r.data)
  );

  const tracks = tracksData?.results || [];

  // Called by TrackTable after every save/delete.
  // Ask the backend directly whether the album still exists — if it's gone (404), go back.
  const handleTrackSave = React.useCallback(async () => {
    try {
      await getAlbumDetail(id);
    } catch (err) {
      if (err.response?.status === 404) navigate(-1);
    }
  }, [id, navigate]);

  const startEditTitle = () => {
    setTitleValue(album.title);
    setEditingTitle(true);
    setTimeout(() => titleInputRef.current?.focus(), 0);
  };

  const commitEditTitle = async () => {
    const trimmed = titleValue.trim();
    setEditingTitle(false);
    if (!trimmed || trimmed === album.title) return;
    try {
      const { data: result } = await mergeAlbumInto(id, trimmed);
      if (result.id !== parseInt(id, 10)) {
        // Merged into a different album — this one is gone, go back
        queryClient.invalidateQueries('albums');
        navigate(-1);
      } else {
        // Simple rename — stay on page
        queryClient.invalidateQueries(['album', id]);
        queryClient.invalidateQueries('albums');
      }
    } catch (err) {
      alert('Failed to rename album.');
    }
  };

  const handleTitleKeyDown = (e) => {
    if (e.key === 'Enter') commitEditTitle();
    else if (e.key === 'Escape') setEditingTitle(false);
  };

  if (albumLoading) return <div style={{ color: 'var(--text-muted)', padding: 32 }}>Loading…</div>;

  return (
    <div>
      <div className="page-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <h1>Album</h1>
          <NavControls />
        </div>
      </div>

      {/* Album header — big art + info */}
      <div className="album-detail-header">
        {/* Big album art */}
        <div className="album-detail-art">
          {album.cover_art_url
            ? <img src={album.cover_art_url} alt={album.title}
                style={{ width: '100%', height: '100%', borderRadius: 12, objectFit: 'cover', boxShadow: '0 8px 32px rgba(0,0,0,0.5)' }} />
            : <div style={{ width: '100%', height: '100%', borderRadius: 12, background: 'var(--bg2)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 64 }}>💿</div>
          }
        </div>

        {/* Album info */}
        <div style={{ flex: 1, paddingTop: 8 }}>
          <div className="album-detail-label" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '.1em', color: 'var(--text-muted)', marginBottom: 8 }}>Album</div>

          {editingTitle ? (
            <input
              ref={titleInputRef}
              value={titleValue}
              onChange={e => setTitleValue(e.target.value)}
              onBlur={commitEditTitle}
              onKeyDown={handleTitleKeyDown}
              style={{
                fontSize: 32,
                fontWeight: 700,
                margin: '0 0 8px',
                width: '100%',
                background: 'var(--bg)',
                border: '1px solid var(--accent)',
                borderRadius: 6,
                color: 'var(--text)',
                padding: '2px 8px',
                display: 'block',
              }}
            />
          ) : (
            <h2
              style={{ fontSize: 32, fontWeight: 700, margin: '0 0 8px', cursor: isAdmin ? 'text' : 'default' }}
              title={isAdmin ? 'Click to rename' : undefined}
              onClick={isAdmin ? startEditTitle : undefined}
            >
              {album.title}
            </h2>
          )}

          <div style={{ fontSize: 18, color: 'var(--text-muted)', marginBottom: 4, display: 'flex', alignItems: 'center', gap: 8 }}>
            {album.is_compilation ? 'Various Artists' : album.artist_name}
            {album.is_compilation && (
              <span style={{ fontSize: 11, background: 'var(--accent)', color: '#fff', borderRadius: 4, padding: '2px 6px' }}>Compilation</span>
            )}
          </div>
          <div style={{ fontSize: 14, color: 'var(--text-muted)', marginBottom: 24 }}>
            {album.year && `${album.year} · `}{tracks.length} tracks
          </div>

          {/* Action buttons */}
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <button
              className="btn btn-secondary"
              onClick={() => navigate(-1)}
            >
              ← Back to Albums
            </button>
            {isAdmin && (
              <button
                className={`btn ${album.is_compilation ? 'btn-primary' : 'btn-secondary'}`}
                onClick={toggleCompilation}
                disabled={merging}
                title="Merge all albums with this title into one compilation under Various Artists"
              >
                📀 {merging ? 'Merging…' : album.is_compilation ? 'Compilation ✓' : 'Mark as Compilation'}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Track list */}
      {tracksLoading
        ? <p style={{ color: 'var(--text-muted)' }}>Loading tracks…</p>
        : <TrackTable
            tracks={tracks}
            queryKey={['album-tracks', id]}
            editable={isAdmin}
            deletable={isAdmin ? 'library' : null}
            onSave={handleTrackSave}
        />
      }
    </div>
  );
}
